<template>
  <div>
    <article class="tw-relative">
      <div class="tw-flex tw-gap-3">
        <div class="tw-h-10 tw-w-10 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full">
          <RelationshipUserImage
            v-if="user"
            :user="user"
            thumb
            class="tw-h-full tw-w-full tw-object-cover"
          />
        </div>

        <div class="tw-flex-grow">
          <div class="tw-relative tw-rounded-lg tw-bg-black/5 tw-px-4 tw-py-2">
            <div class="tw-flex tw-justify-between">
              <router-link :to="'/user/' + comment.user_id" class="tw-text-sm">
                <span class="tw-font-bold">
                  <template v-if="user"> {{ user.firstname }} {{ user.lastname }} </template>

                  <template v-else> - </template>
                </span>
              </router-link>

              <div>
                <Dropdown v-if="(reportURL && !isOwner) || isOwner">
                  <template #content>
                    <div class="options card padding">
                      <div class="option tw-cursor-pointer" v-if="isOwner">
                        <span @click="state.deleteQuery = true">
                          {{ $t('Delete') }}
                        </span>
                      </div>
                      <div v-if="reportURL && !isOwner" class="option tw-cursor-pointer">
                        <nuxt-link :to="`${reportURL}/${comment.id}/report`" :append="true">
                          {{ $t('Report') }}
                        </nuxt-link>
                      </div>
                    </div>
                  </template>
                </Dropdown>
              </div>
            </div>

            <div class="comment-content">
              <div>
                <p
                  v-linkify:options="{
                    target: '_blank'
                  }"
                  :key="comment.text"
                  class="linkified-text tw-break-all tw-text-sm"
                >
                  {{ comment.text }}
                </p>
              </div>
            </div>
          </div>

          <span class="tw-text-xs tw-leading-none tw-text-riser-mainText/50">
            {{ formatTimeDifference(new Date(comment.created_at)) }}
          </span>
        </div>
      </div>
    </article>

    <!-- DELETE CONFIRMATION popup -->
    <ConfirmDialog
      v-if="state.deleteQuery"
      @close="state.deleteQuery = false"
      :title="$t('Are-you-sure-you-want-to-delete-your-comment?')"
      :options="[
        {
          text: $t('Yes'),
          onClick: () => deleteComment(comment.id),
          class: 'button black-button'
        },
        {
          text: $t('No'),
          onClick: () => (state.deleteQuery = false),
          class: 'button '
        }
      ]"
    />
  </div>
</template>

<script setup lang="ts">
import { type PostingComment } from '~/types/postings'

const props = defineProps<{
  reportURL?: string
  comment: PostingComment
  isLikeable?: boolean
  endpoint: string
}>()

const state = reactive({
  loading: false,
  deleteQuery: false,
  message: props.comment.text
})

const emit = defineEmits(['update'])

const authStore = useAuthStore()

const { deleteComment: deleteCommentInternal } = useCommentsService()

const user = useUser(props.comment.user_id)

const isOwner = computed(() => {
  return authStore.session_data.user.id === props.comment.user_id
})

const deleteComment = async (id: number) => {
  const endPoint = props.endpoint ? props.endpoint + '/' + id : 'comments/' + id
  await deleteCommentInternal(endPoint)
  emit('update')
  state.deleteQuery = false
}
</script>

<style scoped>
.linkified-text {
  @apply tw-whitespace-pre-wrap;
}

.linkified-text :deep(a) {
  @apply tw-text-riser-blue;
}
</style>

<style lang="sass" scoped>
$posterPicSize: 40px

:deep(.like-icon svg)
  height: 20px

:deep(.like-count)
  color: rgb(var(--main-text-color))
  margin-right: 0

:deep(.posterPic)
  min-width: $posterPicSize
  width: $posterPicSize
  height: $posterPicSize

.options
  position: absolute
  z-index: 20
  left: 50%
  transform: translateX(-50%)
</style>
